import clsx from 'clsx';

import type { MaturityResponse } from '../api';

interface MaturityGaugeProps {
  maturity: MaturityResponse;
  /**
   * When true and `canPublish` is true and warnings are present, render the
   * "Publicar de todas formas" button alongside the normal publish flow.
   */
  showForcePublish?: boolean;
  onPublish?: () => void;
  onForcePublish?: () => void;
  busy?: boolean;
}

interface Tone {
  ring: string;
  text: string;
  fill: string;
  label: string;
}

function toneFor(score: number, canPublish: boolean): Tone {
  if (score < 50) {
    return {
      ring: 'ring-rose-200',
      text: 'text-rose-700',
      fill: 'bg-rose-500',
      label: 'No recomendado para producción',
    };
  }
  if (score < 75) {
    return {
      ring: 'ring-amber-200',
      text: 'text-amber-700',
      fill: 'bg-amber-500',
      label: canPublish
        ? 'Puede publicar con advertencias'
        : 'Bloqueado — revisa los avisos',
    };
  }
  return {
    ring: 'ring-emerald-200',
    text: 'text-emerald-700',
    fill: 'bg-emerald-500',
    label: 'Listo para publicar',
  };
}

/**
 * Visual maturity gauge.
 *
 * Decision D2 (PHASE_6.md): when `canPublish` is true AND warnings are present,
 * the consumer may render an additional "Publicar de todas formas" button.
 * That button does NOT show up when there are blockers (the backend rejects
 * the publish call without `ForcePublishDespiteWarnings`, and warnings alone
 * don't constitute a block).
 */
export function MaturityGauge({
  maturity,
  showForcePublish = false,
  onPublish,
  onForcePublish,
  busy = false,
}: MaturityGaugeProps): JSX.Element {
  const score = Math.max(0, Math.min(100, Math.round(maturity.score)));
  const tone = toneFor(score, maturity.canPublish);
  const hasBlockers = maturity.blockers.length > 0;
  const hasWarnings = maturity.warnings.length > 0;

  return (
    <div className="space-y-5" data-testid="maturity-gauge">
      <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-center">
        <div
          className={clsx(
            'relative flex h-32 w-32 shrink-0 items-center justify-center rounded-full bg-white ring-8 ring-inset',
            tone.ring,
          )}
          aria-label={`Score de madurez ${score} sobre 100`}
        >
          <span className={clsx('text-3xl font-semibold tabular-nums', tone.text)}>
            {score}
          </span>
          <span className="absolute bottom-3 text-xs uppercase tracking-wide text-slate-400">
            / 100
          </span>
        </div>
        <div>
          <p className={clsx('text-lg font-semibold', tone.text)} data-testid="maturity-label">
            {tone.label}
          </p>
          <p className="mt-1 text-sm text-slate-500">
            {maturity.samplesProcessed} muestras procesadas · {maturity.fieldsWithHighConfidence}
            /{maturity.totalCandidateFields} campos con alta confianza.
          </p>
        </div>
      </div>

      {hasBlockers ? (
        <section
          className="rounded-md border border-rose-200 bg-rose-50 p-3"
          data-testid="blockers-section"
        >
          <h3 className="text-sm font-semibold text-rose-800">
            Bloqueos para publicación
          </h3>
          <ul className="mt-1 list-inside list-disc text-sm text-rose-900">
            {maturity.blockers.map((blocker, i) => (
              <li key={`blocker-${i}`}>{blocker}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {hasWarnings ? (
        <section
          className="rounded-md border border-amber-200 bg-amber-50 p-3"
          data-testid="warnings-section"
        >
          <h3 className="text-sm font-semibold text-amber-800">Advertencias</h3>
          <ul className="mt-1 list-inside list-disc text-sm text-amber-900">
            {maturity.warnings.map((warning, i) => (
              <li key={`warning-${i}`}>{warning}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {onPublish !== undefined || onForcePublish !== undefined ? (
        <div className="flex flex-wrap justify-end gap-2">
          {showForcePublish && maturity.canPublish && hasWarnings && !hasBlockers && onForcePublish !== undefined ? (
            <button
              type="button"
              className="btn-secondary"
              onClick={onForcePublish}
              disabled={busy}
              data-testid="force-publish-button"
            >
              Publicar de todas formas
            </button>
          ) : null}
          {onPublish !== undefined ? (
            <button
              type="button"
              className="btn-primary"
              onClick={onPublish}
              disabled={busy || !maturity.canPublish}
              data-testid="publish-button"
            >
              {busy ? 'Procesando…' : 'Publicar'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
